import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  Typography,
  Alert,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useProductos, useProductoForm, useInactivarProducto } from '../hooks';
import {
  ProductGrid,
  ProductForm,
  ProductListSearch,
  ProductDetailModal,
  InactivarProductoModal,
} from '../components';
import { Producto } from '../types';
import { ImagenProducto } from '../types/producto.types';
import { ProductoService } from '../services';
import { validarImagenes } from '../utils';

export function ProductosPage() {
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProducto, setSelectedProducto] = useState<Producto | undefined>();
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [detailProducto, setDetailProducto] = useState<Producto | null>(null);
  const [imagenes, setImagenes] = useState<ImagenProducto[]>([]);
  const [imagenesOriginales, setImagenesOriginales] = useState<ImagenProducto[]>([]);
  const [sincronizandoImagenes, setSincronizandoImagenes] = useState(false);
  const [errorImagenes, setErrorImagenes] = useState<string | null>(null);

  // Inactivar producto state
  const {
    modalOpen: inactivarModalOpen,
    selectedProducto: inactivarProducto,
    loading: inactivarLoading,
    error: inactivarError,
    openModal: openInactivarModal,
    closeModal: closeInactivarModal,
    inactivar: performInactivar,
  } = useInactivarProducto();

  // Toast state
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');

  const {
    productos,
    loading: listLoading,
    loadingMore,
    error: listError,
    total,
    searchQuery,
    hasMore,
    updateSearchQuery,
    clearSearch,
    fetchMore,
    refetch,
  } = useProductos();

  const syncImagenes = useCallback(
    async (productoId: number) => {
      try {
        setSincronizandoImagenes(true);
        setErrorImagenes(null);

        // Validar imágenes
        const erroresValidacion = validarImagenes(imagenes);
        if (erroresValidacion.length > 0) {
          setErrorImagenes(erroresValidacion.join('; '));
          return;
        }

        // Identificar cambios
        const imagenesASubir = imagenes.filter((img) => !img.imagenId);
        const imagenesAEliminar = imagenesOriginales.filter(
          (orig) => !imagenes.find((actual) => actual.imagenId === orig.imagenId)
        );

        const erroresSincronizacion: string[] = [];

        // Subir nuevas imágenes
        for (const imagen of imagenesASubir) {
          try {
            await ProductoService.subirImagen(productoId, imagen);
          } catch (err: any) {
            erroresSincronizacion.push(
              `Error subiendo imagen ${imagen.nombre}: ${err.message}`
            );
          }
        }

        // Eliminar imágenes
        for (const imagen of imagenesAEliminar) {
          try {
            if (imagen.imagenId) {
              await ProductoService.eliminarImagen(productoId, imagen.imagenId);
            }
          } catch (err: any) {
            erroresSincronizacion.push(
              `Error eliminando imagen ${imagen.nombre}: ${err.message}`
            );
          }
        }

        if (erroresSincronizacion.length > 0) {
          setErrorImagenes(
            'Producto guardado pero algunos cambios de imágenes fallaron: ' +
              erroresSincronizacion.join('; ')
          );
        } else {
          // Recargar imágenes para confirmar
          const imagenesActualizadas = await ProductoService.getImagenes(productoId);
          setImagenes(imagenesActualizadas);
          setImagenesOriginales(imagenesActualizadas);
        }
      } catch (err: any) {
        setErrorImagenes('Error sincronizando imágenes: ' + err.message);
      } finally {
        setSincronizandoImagenes(false);
      }
    },
    [imagenes, imagenesOriginales]
  );

  const {
    formData,
    loading: formLoading,
    error: formError,
    handleChange,
    handleSubmit: onFormSubmit,
  } = useProductoForm(selectedProducto, async (producto) => {
    // Sincronizar imágenes si es edición
    if (selectedProducto && imagenes.length > 0) {
      await syncImagenes(producto.id);
    }
    setOpenDialog(false);
    setSelectedProducto(undefined);
    setImagenes([]);
    setImagenesOriginales([]);
    setErrorImagenes(null);
    await refetch();
  });

  const handleEditProducto = async (producto: Producto) => {
    setSelectedProducto(producto);
    setErrorImagenes(null);
    try {
      // Cargar imágenes existentes
      const imagenesExistentes = await ProductoService.getImagenes(producto.id);
      setImagenes(imagenesExistentes);
      setImagenesOriginales(imagenesExistentes);
    } catch (err: any) {
      setErrorImagenes('Error al cargar imágenes: ' + err.message);
      setImagenes([]);
      setImagenesOriginales([]);
    }
    setOpenDialog(true);
  };

  const handleDeleteProducto = (id: number) => {
    const producto = productos.find(p => p.id === id);
    if (producto) {
      openInactivarModal(producto);
    }
  };

  const handleConfirmInactivar = async () => {
    if (!inactivarProducto) return;

    try {
      await performInactivar(inactivarProducto);
      setToastMessage('Producto desactivado correctamente');
      setToastSeverity('success');
      setToastOpen(true);
      await refetch();
    } catch (err: any) {
      const errorMessage =
        err.message === 'El producto ya fue desactivado'
          ? 'El producto ya fue desactivado'
          : err.message || 'Error al inactivar producto';
      setToastMessage(errorMessage);
      setToastSeverity('error');
      setToastOpen(true);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    await onFormSubmit(e);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProducto(undefined);
  };

  const handleOpenDetailModal = (producto: Producto) => {
    setDetailProducto(producto);
    setOpenDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setDetailProducto(null);
    setOpenDetailModal(false);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">Gestión de Productos</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/productos/nuevo')}
        >
          Nuevo Producto
        </Button>
      </Stack>

      <ProductListSearch
        value={searchQuery}
        onSearch={updateSearchQuery}
        onClear={clearSearch}
        loading={listLoading}
        resultCount={productos.length}
        total={total}
      />

      {listError && !listLoading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {listError}
          <Button size="small" onClick={() => refetch()}>Reintentar</Button>
        </Alert>
      )}

      <ProductGrid
        productos={productos}
        loading={listLoading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        onEdit={handleEditProducto}
        onDelete={handleDeleteProducto}
        onViewDetails={handleOpenDetailModal}
        onLoadMore={fetchMore}
      />

      <Dialog
        open={!!selectedProducto}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Editar Producto
          </Typography>
          <Button
            variant="text"
            color="inherit"
            size="small"
            startIcon={<CloseIcon />}
            onClick={handleCloseDialog}
            sx={{ minWidth: 'auto' }}
          >
            Cerrar
          </Button>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {errorImagenes && (
            <Alert severity={sincronizandoImagenes ? 'info' : 'warning'} sx={{ mb: 2 }}>
              {errorImagenes}
            </Alert>
          )}
          <ProductForm
            formData={formData}
            loading={formLoading || sincronizandoImagenes}
            error={formError}
            isEditing={!!selectedProducto}
            onChange={handleChange}
            onSubmit={handleFormSubmit}
            onCancel={handleCloseDialog}
            imagenes={imagenes}
            onImagenesChange={setImagenes}
            productoId={selectedProducto?.id}
          />
        </DialogContent>
      </Dialog>

      <ProductDetailModal
        open={openDetailModal}
        producto={detailProducto}
        onClose={handleCloseDetailModal}
        onEdit={handleEditProducto}
      />

      <InactivarProductoModal
        open={inactivarModalOpen}
        producto={inactivarProducto}
        loading={inactivarLoading}
        onConfirm={handleConfirmInactivar}
        onCancel={closeInactivarModal}
      />

      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        message={toastMessage}
      />
    </Container>
  );
}
